import { ArrowLeft, DoorOpen, Hourglass, RefreshCw, TimerOff } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The two full-page lobby states a held join can land in (CR-14). Both keep the Syncra dark
 * card look of RemovedScreen; the media session underneath stays untouched - the waiting
 * screen's cancel tears the session down cleanly, the rejected screen's retry starts a fresh
 * join attempt from the RoomPage.
 */

/** Props of the waiting screen: the room the user is waiting in front of. */
export interface LobbyWaitingScreenProps {
  readonly roomName: string;
  /** Clean leave: the cancel button tears the whole session down (media + realtime). */
  readonly onCancel: () => void;
}

/**
 * The «Вы в комнате ожидания» screen a participant sees after the WS handshake answered
 * lobby_wait. The pulsing dot carries the "the moderator knows" feel; no timer is shown - the
 * server's 5-minute deadline would only read as a threat.
 */
export function LobbyWaitingScreen({ roomName, onCancel }: LobbyWaitingScreenProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-foreground">
      <div
        data-testid="lobby-waiting"
        className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-amber-500/25 bg-zinc-900/60 p-8 text-center shadow-2xl shadow-black/50"
      >
        <div className="relative grid h-12 w-12 place-items-center rounded-full bg-amber-500/15">
          {/* The animated pulse: three staggered rings over the static dot (Syncra style). */}
          <span
            aria-hidden
            className="absolute inline-flex h-12 w-12 animate-ping rounded-full bg-amber-400/20"
          />
          <span
            aria-hidden
            className="relative inline-flex h-3 w-3 animate-pulse rounded-full bg-amber-300"
          />
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">{t('lobby.waiting_title')}</h1>
        <p className="text-sm text-zinc-400">{t('lobby.waiting_body')}</p>
        <p
          data-testid="lobby-waiting-room"
          title={roomName}
          className="max-w-full truncate rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[11px] text-zinc-400"
        >
          {roomName}
        </p>
        <button
          type="button"
          data-testid="lobby-cancel"
          onClick={onCancel}
          className="mt-2 flex items-center gap-1.5 rounded-full bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-700"
        >
          <DoorOpen aria-hidden className="h-3.5 w-3.5" />
          {t('lobby.cancel')}
        </button>
      </div>
    </main>
  );
}

/** Which terminal lobby outcome the rejected screen reports. */
export type LobbyRejectionKind = 'denied' | 'timeout';

export interface LobbyRejectedScreenProps {
  readonly kind: LobbyRejectionKind;
  readonly roomName: string;
  /** Try again: tears this attempt down and starts a fresh join (a new lobby_wait). */
  readonly onRetry: () => void;
}

/**
 * The screen after a deny (4407) or a wait that crossed the server deadline (4400): the reason
 * line differs, the affordances do not - try again re-joins, the link leads home. No automatic
 * rejoin: a refused join must not loop.
 */
export function LobbyRejectedScreen({
  kind,
  roomName,
  onRetry,
}: LobbyRejectedScreenProps): JSX.Element {
  const { t } = useTranslation();
  const denied = kind === 'denied';
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-foreground">
      <div
        data-testid="lobby-denied"
        className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-red-500/30 bg-zinc-900/60 p-8 text-center shadow-2xl shadow-black/50"
      >
        <div className="grid h-12 w-12 place-items-center rounded-full bg-red-500/15">
          {denied ? (
            <Hourglass aria-hidden className="h-6 w-6 text-red-300" />
          ) : (
            <TimerOff aria-hidden className="h-6 w-6 text-red-300" />
          )}
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">
          {t(denied ? 'lobby.denied_title' : 'lobby.timeout_title')}
        </h1>
        <p className="text-sm text-zinc-400">
          {t(denied ? 'lobby.denied_body' : 'lobby.timeout_body')}
        </p>
        <p
          title={roomName}
          className="max-w-full truncate rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[11px] text-zinc-400"
        >
          {roomName}
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            data-testid="lobby-retry"
            onClick={onRetry}
            className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-zinc-950 transition-colors hover:bg-zinc-200"
          >
            <RefreshCw aria-hidden className="h-3.5 w-3.5" />
            {t('lobby.retry')}
          </button>
          <a
            href="/"
            className="flex items-center gap-1.5 rounded-full bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-700"
          >
            <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
            {t('room.back_home')}
          </a>
        </div>
      </div>
    </main>
  );
}
